// Balance Sheet export utilities — CSV for unlimited rows, PDF for formatted reports
import { pdf } from "@react-pdf/renderer";
import React from "react";
import BalanceSheetPdf from "@/components/BalanceSheetPdf";

/**
 * Generate and trigger download of a balance sheet CSV.
 * CSV format can handle unlimited rows without page limitations.
 *
 * @param {object} data    — Balance sheet data including organization, customer, entries, summary, dateRange
 * @param {string} filename — e.g. "BalanceSheet_ABC_Company_2024-01-01_to_2024-12-31.csv"
 */
export async function downloadBalanceSheetCsv(data, filename = "balance-sheet.csv") {
  if (!data) throw new Error("Balance sheet data is required");

  try {
    const { organization, customer, entries, summary, dateRange } = data;
    
    // CSV Header
    let csv = "Date,Description,Customer,Debit,Credit,Outstanding,Due Date,Status,Reference\n";
    
    // CSV Rows
    entries.forEach(entry => {
      const row = [
        entry.entry_date || "",
        `"${(entry.description || "").replace(/"/g, '""')}"`,
        `"${(entry.customer_name || "").replace(/"/g, '""')}"`,
        entry.entry_type === 'debit' ? entry.amount : "",
        entry.entry_type === 'credit' ? entry.amount : "",
        entry.reference_type === 'invoice' ? (entry.outstanding || 0) : "",
        entry.due_date || "",
        entry.calculated_status || "",
        `"${(entry.reference_number || "").replace(/"/g, '""')}"`
      ];
      csv += row.join(",") + "\n";
    });
    
    // Summary Section
    csv += "\nSummary\n";
    csv += `Total Debit,${summary.total_debit || 0}\n`;
    csv += `Total Credit,${summary.total_credit || 0}\n`;
    csv += `Total Outstanding,${summary.current_balance || 0}\n`;
    csv += `Overdue Amount,${summary.overdue_amount || 0}\n`;
    
    // Metadata
    csv += "\n\nExport Details\n";
    csv += `Organization,"${(organization?.organization_name || "").replace(/"/g, '""')}"\n`;
    csv += `Customer,"${(customer?.name || "All Customers").replace(/"/g, '""')}"\n`;
    csv += `Date From,${dateRange?.from || "All"}\n`;
    csv += `Date To,${dateRange?.to || "All"}\n`;
    csv += `Export Date,${new Date().toISOString().split('T')[0]}\n`;
    
    // Create and download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = window.document.createElement("a");
    a.href = url;
    a.download = filename;
    window.document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    window.document.body.removeChild(a);
  } catch (error) {
    console.error("CSV generation error:", error);
    throw error;
  }
}

/**
 * Generate and trigger download of a balance sheet PDF.
 * PDF is limited by page size and number of pages.
 *
 * @param {object} data    — Balance sheet data including organization, customer, entries, summary, dateRange
 * @param {string} filename — e.g. "BalanceSheet_ABC_Company_2024-01-01_to_2024-12-31.pdf"
 */
export async function downloadBalanceSheetPDF(data, filename = "balance-sheet.pdf") {
  if (!data) throw new Error("Balance sheet data is required");

  try {
    const blob = await pdf(<BalanceSheetPdf data={data} />).toBlob();
    const url = window.URL.createObjectURL(blob);
    const a = window.document.createElement("a");
    a.href = url;
    a.download = filename;
    window.document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    window.document.body.removeChild(a);
  } catch (error) {
    console.error("PDF generation error:", error);
    throw error;
  }
}
